import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { User, Lock, Eye, EyeOff, ShieldCheck, Zap, BarChart3, CheckCircle2 } from "lucide-react";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // --- CHECK IF ALREADY LOGGED IN ON LOAD ---
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      navigate("/");
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await axios.post("/api/login", {
        email: email.trim(),
        password: password
      });

      if (response.data.success && response.data.user) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userInfo", JSON.stringify(response.data.user));
        navigate("/");
      } else {
        throw new Error("Invalid server response");
      }
    } catch (err) {
      if (!err.response) {
        setError("Unable to connect to server. Please check your network connection or server status.");
      } else {
        setError(err.response?.data?.message || "Invalid Email or Password");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      {/* Ambient Glow Orbs */}
      <div className="ambient-glow glow-top-left"></div>
      <div className="ambient-glow glow-bottom-right"></div>

      {/* Huge Light QC Background Watermark */}
      <div className="qc-watermark">QC</div>

      {/* CENTERED CONTAINER */}
      <div className="centered-login-container">
        
        {/* CENTERED LIGHT GLASSMORPHIC CARD */}
        <div className="centered-card">
          
          {/* Header */}
          <div className="brand-header">
            {/* GoSource Official Logo */}
            <div className="brand-logo-box">
              <img 
                src={`${process.env.PUBLIC_URL || ""}/logo.png`} 
                alt="GoSource Logo" 
                className="brand-logo-img" 
              />
            </div>

            <h1 className="main-title">
               <span className="title-highlight">GoSource </span>Operations
            </h1>

            <p className="main-subtitle">
              Sign in to access your appraisal quality control & reviewer management dashboard
            </p>
          </div>

          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <div>{error}</div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleLogin} className="login-form">
            
            {/* Email Field */}
            <div className="input-group">
              <label className="input-label">Email Address</label>
              <div className="input-wrapper">
                <User className="input-icon" size={18} />
                <input
                  type="text"
                  placeholder="name@mortgage.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="styled-input"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="input-group">
              <label className="input-label">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="styled-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="eye-toggle-btn"
                  tabIndex="-1"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="submit-button"
            >
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner"></span> Signing in...
                </span>
              ) : (
                "Sign In to GoOperations"
              )}
            </button>
          </form>

          {/* Feature Badges Row */}
          <div className="feature-badges-row">
            <div className="feature-badge">
              <Zap size={14} color="#2563eb" />
              <span>Real-Time SLA</span>
            </div>
            <div className="feature-badge">
              <BarChart3 size={14} color="#2563eb" />
              <span>Live Productivity</span>
            </div>
            <div className="feature-badge">
              <ShieldCheck size={14} color="#2563eb" />
              <span>QC & Revised Scoping</span>
            </div>
          </div>

          {/* Security Footer Tag */}
          <div className="security-footer">
            <CheckCircle2 size={14} color="#16a34a" />
            <span>© {new Date().getFullYear()} GoSource All rights reserved</span>
          </div>

        </div>

      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .login-wrapper {
          min-height: 100vh;
          width: 100vw;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #1d4ed8;
          background-image: 
            radial-gradient(at 15% 15%, #3b82f6 0px, transparent 50%),
            radial-gradient(at 85% 85%, #0f172a 0px, transparent 60%),
            radial-gradient(at 50% 50%, #2563eb 0px, transparent 100%),
            url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          color: #f8fafc;
          position: relative;
          overflow: hidden;
          padding: 40px 20px;
          box-sizing: border-box;
        }

        .ambient-glow {
          position: absolute;
          border-radius: 50%;
          filter: blur(110px);
          opacity: 0.35;
          pointer-events: none;
        }
        .glow-top-left {
          width: 500px;
          height: 500px;
          background: #60a5fa;
          top: -120px;
          left: -120px;
        }
        .glow-bottom-right {
          width: 600px;
          height: 600px;
          background: #1e3a8a;
          bottom: -160px;
          right: -160px;
        }

        .qc-watermark {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 38vw;
          font-weight: 900;
          color: rgba(255, 255, 255, 0.05);
          letter-spacing: 20px;
          user-select: none;
          pointer-events: none;
          z-index: 2;
          font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
          white-space: nowrap;
          text-shadow: 0 0 60px rgba(255, 255, 255, 0.02);
        }

        .centered-login-container {
          position: relative;
          z-index: 10;
          width: 100%;
          max-width: 480px;
          display: flex;
          justify-content: center;
        }

        .centered-card {
          width: 100%;
          background: rgba(255, 255, 255, 0.94);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.8);
          border-radius: 24px;
          padding: 44px 40px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 40px rgba(37, 99, 235, 0.2);
          box-sizing: border-box;
        }

        .brand-header {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          margin-bottom: 28px;
        }

        .brand-logo-box {
          width: 64px;
          height: 64px;
          border-radius: 18px;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 16px;
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.2);
          border: 1px solid rgba(255, 255, 255, 0.8);
        }

        .brand-logo-img {
          width: 44px;
          height: 44px;
          object-fit: contain;
        }

        .main-title {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
        }

        .title-highlight {
          color: #2563eb;
        }

        .main-subtitle {
          font-size: 13px;
          color: #64748b;
          margin: 0;
          line-height: 1.5;
        }

        .error-alert {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          margin-bottom: 22px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
        }

        .login-form {
          margin-bottom: 24px;
        }

        .input-group {
          margin-bottom: 18px;
        }

        .input-label {
          display: block;
          font-size: 12px;
          font-weight: 700;
          color: #334155;
          margin-bottom: 6px;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 14px;
          color: #64748b;
          pointer-events: none;
        }

        .styled-input {
          width: 100%;
          padding: 13px 14px 13px 44px;
          background-color: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          color: #0f172a;
          font-size: 14px;
          font-weight: 500;
          outline: none;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .styled-input::placeholder {
          color: #94a3b8;
        }

        .styled-input:focus {
          background-color: #ffffff;
          border-color: #2563eb;
          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.15);
        }

        .eye-toggle-btn {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }
        .eye-toggle-btn:hover {
          color: #0f172a;
        }

        .submit-button {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 6px 20px rgba(37, 99, 235, 0.35);
          transition: all 0.2s ease;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(37, 99, 235, 0.45);
          background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
        }

        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .spinner {
          width: 14px;
          height: 14px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: #ffffff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .feature-badges-row {
          display: flex;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 1px solid #f1f5f9;
        }

        .feature-badge {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 6px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 10px;
          font-weight: 700;
          color: #475569;
          text-align: center;
        }

        .security-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
        }

        @media (max-width: 480px) {
          .centered-card {
            padding: 30px 20px;
          }
          .feature-badges-row {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
